import type {GigShellData} from './shellTypes';
import type {Deliverable} from '../types'; // kept in BuildGigTabsInput for call-site compatibility

export interface GigTab {
  key: string;
  label: string;
  to: string;
  isReact: boolean;
  accepted?: number;
  required?: number;
  showActionIndicator?: boolean;
  active?: boolean;
}

export interface GigTabSection {
  key: string;
  label: string;
  tabs: GigTab[];
}

export interface BuildGigTabsInput {
  gigId: string;
  gig: GigShellData;
  deliverables: Deliverable[];
  inAcceptedState: boolean;
  inProgress: boolean; // kept for call-site compatibility
  activeKind?: string;
}

export function buildGigTabs(input: BuildGigTabsInput): GigTabSection[] {
  const {gigId, gig, inAcceptedState, activeKind} = input;
  const sections: GigTabSection[] = [];

  if (inAcceptedState) {
    // ── Prep section ────────────────────────────────────────────────────────────
    // Ember navigation.hbs:230 — `{{#if this.hasFulfillment}}` — INSIDE inAcceptedState block
    // navigation.js:10 — hasFulfillment: or('args.gig.{shipment.id,giftCode.id}')
    if (gig.hasShipment || gig.hasGiftCode) {
      const prepTabs: GigTab[] = [];

      // navigation.hbs:238 — `{{#if @gig.shipment}}`
      if (gig.hasShipment) {
        prepTabs.push({
          key: 'shipping',
          label: 'Shipping',
          to: `/gigs/${gigId}/prep/shipping`,
          isReact: true,
          active: activeKind === 'shipping',
        });
      }

      // navigation.hbs:249 — `{{#if @gig.giftCode}}`
      if (gig.hasGiftCode) {
        prepTabs.push({
          key: 'gift-code',
          label: 'Gift Code',
          // Ember route name is `gift-codes` but its path is `/gift-code` (ember/app/router.js).
          to: `/gigs/${gigId}/prep/gift-code`,
          isReact: true,
          active: activeKind === 'gift-code',
        });
      }

      sections.push({key: 'prep', label: 'Prep', tabs: prepTabs});
    }
  }

  // ── Information section ───────────────────────────────────────────────────
  // Ember navigation.hbs:262 — always rendered (outside the inAcceptedState block)
  // i18n 'header' = 'Information', 'brief.title' = 'Gig Brief', 'application.title' = 'Your Application'
  sections.push({
    key: 'information',
    label: 'Information',
    tabs: [
      {
        key: 'brief',
        label: 'Gig Brief',
        to: `/gigs/${gigId}/brief`,
        isReact: true,
        active: activeKind === 'brief',
      },
      {
        key: 'application',
        label: 'Your Application',
        to: `/gigs/${gigId}/proposal`,
        isReact: true,
        active: activeKind === 'application',
      },
    ],
  });

  // ── Affiliate section ─────────────────────────────────────────────────────
  // Ember navigation.hbs:286 — `{{#if (not (is-empty @gig.campaign.commissionRate))}}`
  if (gig.commissionRate != null) {
    sections.push({
      key: 'affiliate',
      label: 'Affiliate',
      tabs: [
        {
          key: 'affiliate-income',
          label: 'Affiliate Income',
          to: `/gigs/${gigId}/affiliate-income`,
          isReact: true,
          active: activeKind === 'affiliate-income',
        },
      ],
    });
  }

  return sections;
}
